import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { API } from "../App";
import { toast } from "sonner";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { ArrowRight, ArrowLeft, Loader2, MailCheck } from "lucide-react";

const DEFAULT_LOGO = "/logo-qasesores.png";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [settings, setSettings] = useState({
    login_logo_url: DEFAULT_LOGO,
    login_background_color: "#E8E6E6",
    primary_color: "#0C314C",
    accent_color: "#45E69A",
  });

  useEffect(() => {
    axios.get(`${API}/config/personalization`)
      .then(res => setSettings(prev => ({ ...prev, ...res.data })))
      .catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Ingresa tu correo electrónico");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      const msg = err.response?.data?.detail || "Ocurrió un error. Inténtalo de nuevo.";
      toast.error(typeof msg === "string" ? msg : "Ocurrió un error. Inténtalo de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  const logoUrl = settings.login_logo_url || DEFAULT_LOGO;

  return (
    <div className="min-h-screen flex" data-testid="forgot-password-page">
      <div className="hidden lg:flex lg:w-2/3 p-12 flex-col justify-center items-start" style={{ backgroundColor: settings.login_background_color }}>
        <img src={logoUrl} alt="Logo" className="h-72 w-auto" />
      </div>

      <div className="w-full lg:w-1/3 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md space-y-8">
          <div className="lg:hidden flex items-center justify-center mb-8">
            <img src={logoUrl} alt="Logo" className="h-32 w-auto" />
          </div>

          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="space-y-1">
              <CardTitle className="text-2xl font-bold font-['Manrope']" style={{ color: settings.primary_color }}>
                Recuperar contraseña
              </CardTitle>
              <CardDescription className="text-slate-500">
                {sent
                  ? "Revisa tu correo electrónico"
                  : "Ingresa tu correo y te enviaremos un enlace para restablecer tu contraseña"}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {sent ? (
                <div className="space-y-6" data-testid="forgot-password-success">
                  <div className="flex items-start gap-3 rounded-lg bg-emerald-50 border border-emerald-200 p-4">
                    <MailCheck className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
                    <p className="text-sm text-emerald-800">
                      Si el correo está registrado, recibirás un enlace para restablecer tu contraseña.
                      El enlace es válido por <strong>1 hora</strong>. Revisa también tu carpeta de spam.
                    </p>
                  </div>
                  <Link to="/login">
                    <Button variant="outline" className="w-full" data-testid="back-to-login-button">
                      <ArrowLeft className="w-4 h-4 mr-2" /> Volver a iniciar sesión
                    </Button>
                  </Link>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-slate-700">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="tu@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      data-testid="forgot-email-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full text-white font-medium"
                    style={{ backgroundColor: settings.primary_color }}
                    disabled={loading}
                    data-testid="forgot-submit-button"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ArrowRight className="w-4 h-4 mr-2" />}
                    {loading ? "Enviando..." : "Enviar enlace"}
                  </Button>
                  <div className="text-center pt-2">
                    <Link to="/login" className="text-sm text-slate-500 hover:text-slate-700 inline-flex items-center gap-1" data-testid="forgot-back-link">
                      <ArrowLeft className="w-3.5 h-3.5" /> Volver a iniciar sesión
                    </Link>
                  </div>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
